/**
 * ProviderOnboardingPage.tsx — Stripe Connect Express onboarding for tenants
 *
 * Guides the tenant admin through connecting their Stripe Express account.
 * States: Not connected → Onboarding in progress → Pending verification → Connected
 *
 * @since 2026-06-20 (SaaS conversion — Express Connect rewrite)
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';
import AdminLayout from '../components/AdminLayout';

interface ConnectStatus {
  stripeAccountId: string | null;
  onboardingStatus: 'pending' | 'in_progress' | 'pending_verification' | 'complete';
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  businessName: string | null;
  detailsSubmitted: boolean;
  stripeMode?: 'test' | 'live';
}

function StatusBadge({ enabled, label }: { enabled: boolean; label: string }) {
  return (
    <div className="rounded-lg bg-white/70 px-4 py-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`mt-1 text-sm font-medium ${enabled ? 'text-green-700' : 'text-amber-600'}`}>
        {enabled ? 'Enabled' : 'Not yet'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Direct API keys (BYO) — per-tenant test/live secret keys + mode toggle
// ---------------------------------------------------------------------------

interface ProviderKeyConfig {
  stripeMode: 'test' | 'live';
  hasTestKey: boolean;
  hasLiveKey: boolean;
  hasWebhookSecret: boolean;
  configured: boolean;
  error: { code: string; mode: string; message: string } | null;
}

function DirectKeysCard({
  token,
  apiBaseUrl,
  orgId,
}: {
  token: string | null;
  apiBaseUrl: string;
  orgId: string | undefined;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'test' | 'live'>('test');
  const [testKey, setTestKey] = useState('');
  const [liveKey, setLiveKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // AD-20260630-REVEAL: Reveal state for viewing saved keys (matches platform UI)
  const [revealedKey, setRevealedKey] = useState<{ type: string; value: string } | null>(null);
  const [revealingType, setRevealingType] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['org', orgId, 'provider-keys'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/provider`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const body = (await res.json()) as { provider: ProviderKeyConfig | null };
      return body.provider;
    },
    enabled: !!token && !!orgId,
  });

  React.useEffect(() => {
    if (data?.stripeMode) setMode(data.stripeMode);
  }, [data?.stripeMode]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      setSaved(false);
      const payload: Record<string, string> = { mode };
      if (testKey) payload.test_key = testKey;
      if (liveKey) payload.live_key = liveKey;
      if (webhookSecret) payload.webhook_secret = webhookSecret;
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/provider`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(b.error ?? `HTTP ${res.status}`);
      }
    },
    onSuccess: () => {
      setTestKey('');
      setLiveKey('');
      setWebhookSecret('');
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'provider-keys'] });
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  // AD-20260630-REVEAL: Reveal a saved key via the new endpoint
  const revealMutation = useMutation({
    mutationFn: async (keyType: 'test_key' | 'live_key' | 'webhook_secret') => {
      setRevealingType(keyType);
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/provider/reveal/${keyType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Reveal failed: ${res.status}`);
      return (await res.json()) as { value: string | null };
    },
    onSuccess: (data, keyType) => {
      setRevealedKey({ type: keyType, value: data.value ?? '' });
      setRevealingType(null);
    },
    onError: () => setRevealingType(null),
  });

  const activeConfigured = mode === 'live' ? data?.hasLiveKey : data?.hasTestKey;

  // Helper to render a key field with status badge and reveal button
  function renderKeyField(label: string, keyType: 'test_key' | 'live_key' | 'webhook_secret', hasKey: boolean | undefined, value: string, setter: (v: string) => void, placeholder: string) {
    const isRevealed = revealedKey?.type === keyType;
    return (
      <div>
        <div className="flex items-center justify-between">
          <label className="block text-xs font-medium text-slate-700">{label}</label>
          {hasKey ? (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">✓ Key on file</span>
          ) : (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">No key</span>
          )}
        </div>
        {isRevealed ? (
          <div className="mt-1 space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={revealedKey!.value}
                className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm text-slate-700"
              />
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(revealedKey!.value)}
                className="shrink-0 rounded-lg border border-slate-300 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50"
              >Copy</button>
            </div>
            <button type="button" onClick={() => setRevealedKey(null)} className="text-xs text-slate-400 hover:text-slate-600">Hide</button>
          </div>
        ) : (
          <div className="mt-1 flex gap-2">
            <input
              type="password"
              value={value}
              onChange={(e) => setter(e.target.value)}
              placeholder={hasKey ? '•••••••• (leave blank to keep)' : placeholder}
              autoComplete="off"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
            />
            {hasKey && (
              <button
                type="button"
                onClick={() => revealMutation.mutate(keyType)}
                disabled={revealingType === keyType}
                className="shrink-0 rounded-lg border border-slate-300 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                title="View saved key"
              >
                {revealingType === keyType ? '...' : '👁 View'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-xl border border-slate-200 p-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Direct API Keys</h2>
        <p className="mt-1 text-sm text-slate-500">
          Bring your own Stripe secret keys instead of (or alongside) Connect. Keys are encrypted
          at rest and also saved to the Secret Hub. There is no shared/platform fallback — each
          organization must configure its own keys for the mode it transacts in.
        </p>
      </div>

      {/* Status banner */}
      {data && !data.configured && data.error && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3">
          <p className="text-sm font-semibold text-red-800">⚠ Stripe not configured</p>
          <p className="mt-0.5 text-sm text-red-700">{data.error.message}</p>
        </div>
      )}
      {data && data.configured && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
          ✓ Configured for {data.stripeMode} mode. {data.hasTestKey && 'Test key ✓'} {data.hasLiveKey && 'Live key ✓'} {data.hasWebhookSecret && 'Webhook ✓'}
        </div>
      )}

      {/* Mode toggle with key status */}
      <div>
        <label className="block text-sm font-medium text-slate-700">Active Mode</label>
        <div className="mt-2 flex gap-3">
          <button
            type="button"
            onClick={() => setMode('test')}
            className={`flex-1 rounded-lg border p-3 text-center transition ${mode === 'test' ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <p className="text-sm font-semibold text-slate-800">🧪 Test</p>
            <p className={`mt-0.5 text-[11px] ${data?.hasTestKey ? 'text-emerald-600 font-medium' : 'text-slate-400'}`}>
              {data?.hasTestKey ? '✓ Key on file' : 'No key set'}
            </p>
          </button>
          <button
            type="button"
            onClick={() => setMode('live')}
            className={`flex-1 rounded-lg border p-3 text-center transition ${mode === 'live' ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <p className="text-sm font-semibold text-slate-800">🔴 Live</p>
            <p className={`mt-0.5 text-[11px] ${data?.hasLiveKey ? 'text-emerald-600 font-medium' : 'text-slate-400'}`}>
              {data?.hasLiveKey ? '✓ Key on file' : 'No key set'}
            </p>
          </button>
        </div>
      </div>

      {renderKeyField('Test Secret Key', 'test_key', data?.hasTestKey, testKey, setTestKey, 'sk_test_...')}
      {renderKeyField('Live Secret Key', 'live_key', data?.hasLiveKey, liveKey, setLiveKey, 'sk_live_...')}
      {renderKeyField('Webhook Signing Secret', 'webhook_secret', data?.hasWebhookSecret, webhookSecret, setWebhookSecret, 'whsec_...')}

      {/* Webhook URL help */}
      {orgId && (
        <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
          <p className="font-medium text-slate-700">Webhook Setup:</p>
          <p className="mt-1"><strong>Option A — Auto-register (recommended):</strong> Use the "Register Webhook" button in the Stripe Sync card below. This creates a <strong>Connect webhook</strong> on the platform account that receives events from all connected accounts.</p>
          <p className="mt-2"><strong>Option B — Manual:</strong></p>
          <p className="mt-1">1. Go to <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener" className="text-cyan-600 underline">Stripe Dashboard → Webhooks</a></p>
          <p>2. Click "Add endpoint"</p>
          <p>3. Set the endpoint URL to:</p>
          <code className="mt-1 block rounded bg-slate-100 px-2 py-1 font-mono text-[11px] text-slate-700 break-all">
            {apiBaseUrl}/webhooks/stripe
          </code>
          <p>4. Select "Connect" (events from all connected accounts)</p>
          <p>5. Select events: <code className="font-mono">payment_intent.succeeded</code>, <code className="font-mono">invoice.payment_succeeded</code>, <code className="font-mono">customer.subscription.updated</code></p>
          <p>6. Copy the "Signing secret" (starts with <code className="font-mono">whsec_</code>) and paste it above</p>
        </div>
      )}

      {saveError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{saveError}</div>
      )}
      {saved && !saveError && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">Saved.</div>
      )}

      <button
        type="button"
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending || (!testKey && !liveKey && !webhookSecret && mode === data?.stripeMode)}
        className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
      >
        {saveMutation.isPending ? 'Saving…' : `Save (activate ${mode} mode)`}
      </button>
      {mode === 'live' && !activeConfigured && (
        <p className="text-xs text-amber-600">Live mode selected but no live key on file — add one above to transact.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email configuration (BYO) — per-tenant outbound email provider
// ---------------------------------------------------------------------------

interface EmailConfig {
  provider: string;
  fromAddress?: string | null;
  isActive: boolean;
  hasApiKey?: boolean;
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpUser?: string | null;
  hasSmtpPass?: boolean;
}

const EMAIL_PROVIDERS: { value: string; label: string; hint: string }[] = [
  { value: 'platform', label: 'Platform default', hint: 'Use the platform-configured email transport.' },
  { value: 'auth', label: 'Rule42 Auth relay', hint: 'Send through the rule42-auth email relay.' },
  { value: 'resend', label: 'Resend', hint: 'Bring your own Resend API key.' },
  { value: 'sendgrid', label: 'SendGrid', hint: 'Bring your own SendGrid API key.' },
  { value: 'smtp', label: 'Custom SMTP', hint: 'Bring your own SMTP server.' },
];

function EmailConfigCard({
  token,
  apiBaseUrl,
  orgId,
}: {
  token: string | null;
  apiBaseUrl: string;
  orgId: string | undefined;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState('platform');
  const [fromAddress, setFromAddress] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data } = useQuery({
    queryKey: ['org', orgId, 'email-config'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/email-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const body = (await res.json()) as { emailConfig: EmailConfig | null };
      return body.emailConfig;
    },
    enabled: !!token && !!orgId,
  });

  React.useEffect(() => {
    if (data?.provider) setProvider(data.provider);
    if (data?.fromAddress) setFromAddress(data.fromAddress);
    if (data?.smtpHost) setSmtpHost(data.smtpHost);
    if (data?.smtpPort != null) setSmtpPort(String(data.smtpPort));
    if (data?.smtpUser) setSmtpUser(data.smtpUser);
  }, [data?.provider, data?.fromAddress, data?.smtpHost, data?.smtpPort, data?.smtpUser]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      setSaveError(null);
      setSaved(false);
      const payload: Record<string, unknown> = { provider };
      if (fromAddress) payload.from_address = fromAddress;
      if (apiKey) payload.api_key = apiKey;
      if (provider === 'smtp') {
        if (smtpHost) payload.smtp_host = smtpHost;
        if (smtpPort) payload.smtp_port = Number(smtpPort);
        if (smtpUser) payload.smtp_user = smtpUser;
        if (smtpPass) payload.smtp_pass = smtpPass;
      }
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/email-config`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(b.error ?? `HTTP ${res.status}`);
      }
    },
    onSuccess: () => {
      setApiKey('');
      setSmtpPass('');
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'email-config'] });
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  const needsApiKey = provider === 'resend' || provider === 'sendgrid';
  const isSmtp = provider === 'smtp';
  const selected = EMAIL_PROVIDERS.find((p) => p.value === provider);

  return (
    <div className="space-y-4 rounded-xl border border-slate-200 p-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Outbound Email</h2>
        <p className="mt-1 text-sm text-slate-500">
          Choose how this organization sends billing emails (dunning, automation notifications,
          credit alerts). Secrets are encrypted at rest. Leave on “Platform default” to use the
          shared transport.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">Provider</label>
        <select
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          {EMAIL_PROVIDERS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
        {selected && <p className="mt-1 text-xs text-slate-500">{selected.hint}</p>}
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700">From Address</label>
        <input
          type="email"
          value={fromAddress}
          onChange={(e) => setFromAddress(e.target.value)}
          placeholder="billing@yourdomain.com"
          autoComplete="off"
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </div>

      {needsApiKey && (
        <div>
          <label className="block text-xs font-medium text-slate-700">API Key</label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={data?.hasApiKey ? '•••• (leave blank to keep)' : provider === 'resend' ? 're_...' : 'SG....'}
            autoComplete="off"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
          />
        </div>
      )}

      {isSmtp && (
        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-700">SMTP Host</label>
              <input
                type="text"
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                placeholder="smtp.yourhost.com"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="w-28">
              <label className="block text-xs font-medium text-slate-700">Port</label>
              <input
                type="number"
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
                placeholder="587"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700">SMTP Username</label>
            <input
              type="text"
              value={smtpUser}
              onChange={(e) => setSmtpUser(e.target.value)}
              autoComplete="off"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700">SMTP Password</label>
            <input
              type="password"
              value={smtpPass}
              onChange={(e) => setSmtpPass(e.target.value)}
              placeholder={data?.hasSmtpPass ? '•••• (leave blank to keep)' : ''}
              autoComplete="off"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
            />
          </div>
        </div>
      )}

      {saveError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{saveError}</div>
      )}
      {saved && !saveError && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">Saved.</div>
      )}

      <button
        type="button"
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending}
        className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
      >
        {saveMutation.isPending ? 'Saving…' : 'Save email config'}
      </button>
    </div>
  );
}

export default function ProviderOnboardingPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const orgId = activeOrg?.orgId;
  const queryClient = useQueryClient();

  const [actionError, setActionError] = useState<string | null>(null);

  // ─── Fetch live Connect status ───────────────────────────────────────
  // AD-20260630-STATUS-FALLBACK: If the live Stripe status check fails (500),
  // fall back to the provider config from the DB which doesn't call Stripe API.
  // AD-20260702-SYNC-STATUS-FALLBACK: Also try /sync/status as a third fallback.
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['org', orgId, 'stripe-connect-status'],
    queryFn: async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/stripe/connect/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        return res.json() as Promise<ConnectStatus>;
      } catch {
        // Fallback 1: check provider config from DB (doesn't call Stripe API)
        const providerRes = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/provider`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (providerRes.ok) {
          const providerData = await providerRes.json() as { provider: { stripeAccountId: string | null; onboardingStatus: string; stripeMode: string } | null };
          if (providerData.provider?.stripeAccountId) {
            return {
              stripeAccountId: providerData.provider.stripeAccountId,
              onboardingStatus: providerData.provider.onboardingStatus as ConnectStatus['onboardingStatus'],
              chargesEnabled: false,
              payoutsEnabled: false,
              businessName: null,
              detailsSubmitted: false,
              stripeMode: providerData.provider.stripeMode as 'test' | 'live',
            } as ConnectStatus;
          }
        }
        // Fallback 2: check /sync/status (also reads from DB, different code path)
        const syncRes = await fetch(`${apiBaseUrl}/sync/status/${orgId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (syncRes.ok) {
          const syncData = (await syncRes.json()).data as { stripe: { configured: boolean; accountId: string | null; onboardingStatus: string; mode: string } };
          if (syncData.stripe.configured && syncData.stripe.accountId) {
            return {
              stripeAccountId: syncData.stripe.accountId,
              onboardingStatus: syncData.stripe.onboardingStatus as ConnectStatus['onboardingStatus'],
              chargesEnabled: false,
              payoutsEnabled: false,
              businessName: null,
              detailsSubmitted: false,
              stripeMode: syncData.stripe.mode as 'test' | 'live',
            } as ConnectStatus;
          }
        }
        throw new Error('Failed to fetch Connect status');
      }
    },
    enabled: !!token && !!orgId,
    refetchInterval: (query) => {
      const status = query.state.data?.onboardingStatus;
      return status === 'in_progress' || status === 'pending' ? 5000 : false;
    },
    retry: 1,
  });

  const status = data?.onboardingStatus ?? 'pending';

  // ─── Start onboarding (creates Express account + redirects) ──────────
  const onboardMutation = useMutation({
    mutationFn: async () => {
      setActionError(null);
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/stripe/connect/onboard`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        const errMsg = body.detail ?? body.error ?? `HTTP ${res.status}`;
        throw new Error(errMsg);
      }
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err: Error) => setActionError(err.message),
  });

  // ─── Resume onboarding (new account link for incomplete flow) ────────
  const resumeMutation = useMutation({
    mutationFn: async () => {
      setActionError(null);
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/stripe/connect/onboard/refresh`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err: Error) => setActionError(err.message),
  });

  // ─── Open Stripe Express Dashboard ──────────────────────────────────
  const dashboardMutation = useMutation({
    mutationFn: async () => {
      setActionError(null);
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/stripe/connect/dashboard-link`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: ({ url }) => {
      window.open(url, '_blank');
    },
    onError: (err: Error) => setActionError(err.message),
  });

  // ─── Disconnect ─────────────────────────────────────────────────────
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const disconnectMutation = useMutation({
    mutationFn: async () => {
      setActionError(null);
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/stripe/connect/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
    },
    onSuccess: () => {
      setShowDisconnectConfirm(false);
      void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'stripe-connect-status'] });
      void refetch();
    },
    onError: (err: Error) => setActionError(err.message),
  });

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <AdminLayout title="Payment Provider">
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Stripe Connect</h2>
          <p className="mt-1 text-sm text-slate-500">
            Connect your Stripe account to receive payments directly from your customers.
            Rule42 uses Stripe Express to handle onboarding, compliance, and payouts.
          </p>
        </div>

        {/* Billing Tenant ID — easy to find and copy for product configuration */}
        <BillingTenantIdCard token={token} apiBaseUrl={apiBaseUrl} orgId={orgId} />

        {actionError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <p className="font-medium">{actionError}</p>
            <p className="mt-1 text-xs text-red-600">If this persists, try resetting the connection below, then reconnect.</p>
          </div>
        )}

        {/* Reset connection button — clears stale account IDs */}
        {(status === 'complete' || status === 'in_progress' || status === 'pending_verification' || actionError) && (
          <div className="flex justify-end">
            <button
              onClick={() => {
                if (confirm('This will clear the Stripe Connect account ID so you can start fresh. Continue?')) {
                  fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/stripe/connect/disconnect`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                  }).then(() => {
                    void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'stripe-connect-status'] });
                    void refetch();
                    setActionError(null);
                  }).catch(() => {});
                }
              }}
              className="text-xs text-slate-400 hover:text-red-500 underline"
            >
              Reset Connection
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            Loading connection status...
          </div>
        ) : status === 'complete' ? (
          /* ── Connected ────────────────────────────────────────── */
          <div className="space-y-4">
            <div className="rounded-xl border border-green-200 bg-green-50 p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-600">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <div>
                  <h3 className="font-semibold text-green-800">Stripe Connected</h3>
                  {data?.businessName && (
                    <p className="text-sm text-green-700">{data.businessName}</p>
                  )}
                  <p className="text-xs text-green-600 font-mono mt-0.5">
                    {data?.stripeAccountId}
                  </p>
                  <span className="mt-1 inline-block rounded-full bg-green-200 px-2 py-0.5 text-[10px] font-medium text-green-800 uppercase">
                    {data?.stripeMode === 'live' ? '🔴 Live' : '🧪 Test'} Mode
                  </span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <StatusBadge enabled={data?.chargesEnabled ?? false} label="Charges" />
                <StatusBadge enabled={data?.payoutsEnabled ?? false} label="Payouts" />
              </div>

              {(!data?.chargesEnabled || !data?.payoutsEnabled) && (
                <p className="mt-3 text-xs text-amber-600">
                  Some capabilities are not yet enabled. Stripe may need additional verification.
                  Open your Express Dashboard to check requirements.
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => dashboardMutation.mutate()}
                disabled={dashboardMutation.isPending}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {dashboardMutation.isPending ? 'Opening...' : 'Open Stripe Dashboard'}
              </button>
              <button
                onClick={() => setShowDisconnectConfirm(true)}
                className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                Disconnect
              </button>
            </div>

            {showDisconnectConfirm && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="text-sm text-red-700 font-medium">
                  Are you sure you want to disconnect your Stripe account?
                </p>
                <p className="mt-1 text-xs text-red-600">
                  This will delete your Express account and you will no longer be able to accept
                  payments until you reconnect. Existing charges are not affected.
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => disconnectMutation.mutate()}
                    disabled={disconnectMutation.isPending}
                    className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {disconnectMutation.isPending ? 'Disconnecting...' : 'Yes, Disconnect'}
                  </button>
                  <button
                    onClick={() => setShowDisconnectConfirm(false)}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : status === 'in_progress' || status === 'pending_verification' ? (
          /* ── Onboarding in progress ───────────────────────────── */
          <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100 text-yellow-600">
                <svg className="h-5 w-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <div>
                <h3 className="font-semibold text-yellow-800">
                  {status === 'pending_verification' ? 'Pending Verification' : 'Onboarding In Progress'}
                </h3>
                <p className="text-sm text-yellow-600">
                  {status === 'pending_verification'
                    ? 'Stripe is reviewing your account details. This usually takes 1-2 business days.'
                    : 'Your Stripe account setup is incomplete. Continue where you left off.'}
                </p>
              </div>
            </div>

            {status === 'in_progress' && (
              <button
                onClick={() => resumeMutation.mutate()}
                disabled={resumeMutation.isPending}
                className="mt-4 rounded-lg bg-yellow-600 px-4 py-2 text-sm font-medium text-white hover:bg-yellow-700 disabled:opacity-50"
              >
                {resumeMutation.isPending ? 'Redirecting...' : 'Continue Onboarding'}
              </button>
            )}

            {data?.stripeAccountId && (
              <p className="mt-3 text-xs text-yellow-600 font-mono">
                Account: {data.stripeAccountId}
              </p>
            )}
          </div>
        ) : (
          /* ── Not connected ────────────────────────────────────── */
          <div className="rounded-xl border-2 border-dashed border-slate-200 p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <svg className="h-6 w-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <h3 className="mt-4 font-semibold text-slate-700">No Payment Provider Connected</h3>
            <p className="mt-2 text-sm text-slate-500">
              Connect a Stripe Express account to start accepting payments. Stripe handles
              compliance, tax forms, and payouts — you just sell.
            </p>
            <button
              onClick={() => onboardMutation.mutate()}
              disabled={onboardMutation.isPending}
              className="mt-4 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
            >
              {onboardMutation.isPending ? 'Setting up...' : 'Connect Stripe Account'}
            </button>
          </div>
        )}

        <DirectKeysCard token={token} apiBaseUrl={apiBaseUrl} orgId={orgId} />
        <StripeSyncCard token={token} apiBaseUrl={apiBaseUrl} orgId={orgId} connected={status === 'complete' || status === 'in_progress'} />
        <EmailConfigCard token={token} apiBaseUrl={apiBaseUrl} orgId={orgId} />
      </div>
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
// Billing Tenant ID Card — shows the billing tenant ID with copy button
// ---------------------------------------------------------------------------

function BillingTenantIdCard({ token, apiBaseUrl, orgId }: { token: string | null; apiBaseUrl: string; orgId: string | undefined }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const { data } = useQuery({
    queryKey: ['org', orgId, 'billing-tenant-id'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/billing-tenant-id`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      return res.json() as Promise<{ billingTenantId: string | null; billingTenantName: string | null }>;
    },
    enabled: !!token && !!orgId,
  });

  if (!data?.billingTenantId) return <></>;

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-indigo-700">Billing Tenant ID</p>
          <p className="mt-1 font-mono text-sm text-indigo-900 select-all">{data.billingTenantId}</p>
          <p className="mt-1 text-xs text-indigo-500">
            {data.billingTenantName ? `${data.billingTenantName} — ` : ''}Use this ID in your product's platform settings as <code className="font-mono">BILLING_TENANT_ID</code>.
          </p>
        </div>
        <button
          onClick={() => {
            navigator.clipboard?.writeText(data.billingTenantId!);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className="shrink-0 rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stripe Sync Card — trigger sync, view status, register webhooks
// ---------------------------------------------------------------------------

interface SyncStatusData {
  tenant: { id: string; name: string; authOrgId: string };
  stripe: {
    configured: boolean;
    accountId: string | null;
    onboardingStatus: string;
    mode: string;
    webhookSecretConfigured: boolean;
  };
}

function StripeSyncCard({
  token,
  apiBaseUrl,
  orgId,
  connected,
}: {
  token: string | null;
  apiBaseUrl: string;
  orgId: string | undefined;
  connected: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [syncResult, setSyncResult] = useState<string | null>(null);

  // Fetch sync status
  const { data: syncStatus, refetch: refetchStatus } = useQuery<SyncStatusData>({
    queryKey: ['org', orgId, 'sync-status'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/sync/status/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!token && !!orgId,
  });

  // Trigger full sync
  const syncMutation = useMutation({
    mutationFn: async () => {
      setSyncResult(null);
      const res = await fetch(`${apiBaseUrl}/sync/tenant/${orgId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Sync failed: ${res.status}`);
      return res.json();
    },
    onSuccess: (data) => {
      const r = data.data;
      setSyncResult(
        `✓ Synced — Customers: ${r.customers.pulled} pulled, ${r.customers.pushed} pushed, ${r.customers.updated} updated | ` +
        `Subscriptions: ${r.subscriptions.pulled} pulled, ${r.subscriptions.updated} updated | ` +
        `Products: ${r.products.pulled} pulled, ${r.products.updated} updated | ` +
        `Webhook: ${r.webhookEndpoint.registered ? 'registered' : 'not registered'}`,
      );
      void refetchStatus();
      void queryClient.invalidateQueries({ queryKey: ['org', orgId] });
    },
    onError: (err: Error) => setSyncResult(`✗ ${err.message}`),
  });

  if (!connected) {
    return (
      <div className="space-y-4 rounded-xl border border-slate-200 p-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Stripe Sync</h2>
          <p className="mt-1 text-sm text-slate-500">
            Connect your Stripe account first to enable bidirectional sync and webhook registration.
          </p>
        </div>

        {/* Still show sync status if available */}
        {syncStatus && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Stripe Account</p>
              <p className="mt-1 font-mono text-xs text-slate-700 truncate" title={syncStatus.stripe.accountId ?? ''}>
                {syncStatus.stripe.accountId ?? 'Not set'}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Mode</p>
              <p className="mt-1 text-sm font-medium text-slate-700">{syncStatus.stripe.mode}</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Onboarding</p>
              <p className="mt-1 text-sm font-medium text-slate-700">{syncStatus.stripe.onboardingStatus}</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Webhook Secret</p>
              <p className={`mt-1 text-sm font-medium ${syncStatus.stripe.webhookSecretConfigured ? 'text-green-700' : 'text-amber-600'}`}>
                {syncStatus.stripe.webhookSecretConfigured ? '✓ Configured' : '⚠ Not set'}
              </p>
            </div>
          </div>
        )}

        {/* Webhook URL reference */}
        {orgId && (
          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
            <p className="font-medium text-slate-700">Connect Webhook URL:</p>
            <code className="mt-1 block rounded bg-slate-100 px-2 py-1 font-mono text-[11px] text-slate-700 break-all">
              {apiBaseUrl}/webhooks/stripe
            </code>
            <p className="mt-1">A Connect webhook receives events from all connected accounts.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-xl border border-slate-200 p-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Stripe Sync</h2>
        <p className="mt-1 text-sm text-slate-500">
          Keep billing in sync with Stripe. Pulls customers, subscriptions, and products from Stripe
          into the local database, and pushes local-only customers to Stripe.
        </p>
      </div>

      {/* Sync status grid */}
      {syncStatus && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-xs text-slate-500">Stripe Account</p>
            <p className="mt-1 font-mono text-xs text-slate-700 truncate" title={syncStatus.stripe.accountId ?? ''}>
              {syncStatus.stripe.accountId ?? 'Not set'}
            </p>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-xs text-slate-500">Mode</p>
            <p className="mt-1 text-sm font-medium text-slate-700">{syncStatus.stripe.mode}</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-xs text-slate-500">Onboarding</p>
            <p className="mt-1 text-sm font-medium text-slate-700">{syncStatus.stripe.onboardingStatus}</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-xs text-slate-500">Webhook Secret</p>
            <p className={`mt-1 text-sm font-medium ${syncStatus.stripe.webhookSecretConfigured ? 'text-green-700' : 'text-amber-600'}`}>
              {syncStatus.stripe.webhookSecretConfigured ? '✓ Configured' : '⚠ Not set'}
            </p>
          </div>
        </div>
      )}

      {/* Webhook info — Connect webhook is platform-level, not per-org */}
      <div className="rounded-lg border border-slate-200 p-4">
        <div>
          <p className="text-sm font-medium text-slate-700">Stripe Webhook</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {syncStatus?.stripe.webhookSecretConfigured
              ? '✓ Connect webhook is registered. Events from all connected accounts are received.'
              : 'Connect webhook is managed at the platform level. Use "Sync Now" below to register it.'}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            A single Connect webhook on the platform account receives events from all connected accounts. Each event is routed to the correct tenant based on the account field.
          </p>
        </div>
      </div>

      {/* Full sync trigger */}
      <div className="rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700">Full Stripe Sync</p>
            <p className="mt-0.5 text-xs text-slate-500">
              Bidirectionally sync customers, subscriptions, products, and webhook configuration.
            </p>
          </div>
          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="shrink-0 rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
          >
            {syncMutation.isPending ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
        {syncResult && (
          <p className="mt-2 text-xs text-slate-600">{syncResult}</p>
        )}
      </div>

      {/* Webhook URL reference */}
      {orgId && (
        <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
          <p className="font-medium text-slate-700">Connect Webhook URL:</p>
          <code className="mt-1 block rounded bg-slate-100 px-2 py-1 font-mono text-[11px] text-slate-700 break-all">
            {apiBaseUrl}/webhooks/stripe
          </code>
          <p className="mt-1">A single Connect webhook on the platform account receives events from <strong>all connected accounts</strong>. Each event includes the <code className="font-mono">account</code> field identifying which connected account it came from, so events are routed to the correct tenant automatically.</p>
        </div>
      )}
    </div>
  );
}
